import CustomTooltip from '../../../shared/CustomTooltip';
import { zodResolver } from '@hookform/resolvers/zod';
import CloseIcon from '@mui/icons-material/Close';
import {
    Alert,
    Box,
    Button,
    capitalize,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    IconButton,
    InputLabel,
    Radio,
    RadioGroup,
    Stack,
    Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { ModalProps } from '../../../types/modal';
import FormTextField from '../../../shared/FormTextField';
import {
    useGetContractorInvitesQuery,
    useGetContractorsQuery,
    useGetOwnerContractorsQuery,
    useSendContractorInviteMutation,
} from '../../../../app/Slices/SevenDayApiSlice';
import toast from '../../../shared/Toast';
import FormAutocomplete from '../../../shared/FormAutocomplete';
import { CustomSmallListChip } from '../../../../layout/styledcomponents/CustomChip';
import UserRole from '../../../../shared/AuthRoles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

type ContractorOption = {
    value: number;
    label: string;
    id: number;
    type?: string;
};

const Schema = z.object({
    contractorId: z.number().optional(),
    contractor: z.object({
        name: z.string().min(1, { message: 'Company name is required' }),
        email: z.string().email('Enter a valid email'),
        primaryContact: z
            .string()
            .min(1, { message: 'Primary contact is required' }),
    }),
    permissions: z.enum(['readOnly', 'readAndWrite']),
});

type ContractorInviteFormValues = z.infer<typeof Schema>;

interface ContractorInviteFormProps extends ModalProps {}

const ContractorInviteForm = ({ open, onClose }: ContractorInviteFormProps) => {
    const loggedInUser = UserRole();
    const [sendInvite] = useSendContractorInviteMutation();
    const [confirmClose, setConfirmClose] = useState(false);

    const { data: contractors } = useGetContractorsQuery();
    const { data: existingContractors } = useGetOwnerContractorsQuery(
        loggedInUser.company,
        { skip: !loggedInUser?.company },
    );
    const { data: invites } = useGetContractorInvitesQuery();

    const availableContractors = useMemo(() => {
        return (contractors || []).filter(
            (contractor: any) =>
                !invites?.some((invite: any) => invite.id === contractor.id) &&
                !existingContractors?.some((c: any) => c.id === contractor.id),
        );
    }, [contractors, invites, existingContractors]);

    const contractorOptions: ContractorOption[] = useMemo(() => {
        return (availableContractors || []).map((contractor: any) => ({
            value: contractor.id,
            label: contractor.name,
            id: contractor.id,
            type: contractor?.services?.type
                ? capitalize(contractor.services.type)
                : undefined,
        }));
    }, [availableContractors]);

    const methods = useForm<ContractorInviteFormValues>({
        defaultValues: {
            contractorId: undefined,
            contractor: {
                name: '',
                email: '',
                primaryContact: '',
            },
            permissions: 'readOnly',
        },
        resolver: zodResolver(Schema),
    });

    const {
        control,
        reset,
        handleSubmit,
        formState: { isDirty, isSubmitting },
    } = methods;

    const handleClose = () => {
        if (isDirty && !confirmClose) {
            setConfirmClose(true);
            return;
        }

        if (confirmClose) reset();
        setConfirmClose(false);
        onClose();
    };

    const onSubmit = async (data: ContractorInviteFormValues) => {
        try {
            await sendInvite(data).unwrap();
            toast.success('Invitation sent successfully');
            reset();
            onClose();
        } catch (error) {
            toast.error('Error sending invite');
        }
    };

    const labelSx = {
        fontSize: '12px',
        lineHeight: 1.2,
        fontWeight: 700,
    };

    const hasExistingContractors = contractorOptions.length > 0;

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogTitle>
                        Invite contractor
                        <IconButton
                            onClick={handleClose}
                            size='small'
                            aria-label='close'
                            sx={{ color: 'text.primary' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent>
                        <Stack spacing={2}>
                            <Collapse in={confirmClose}>
                                <Alert severity='warning'>
                                    You have unsaved changes. Click 'Cancel'
                                    again to discard them.
                                </Alert>
                            </Collapse>

                            {hasExistingContractors && (
                                <>
                                    <Box>
                                        <Stack
                                            direction='row'
                                            alignItems='center'
                                            spacing={0.5}
                                        >
                                            <InputLabel sx={labelSx}>
                                                Existing contractor on 7DAY
                                            </InputLabel>

                                            <CustomTooltip
                                                title='7DAY has a range of contractors in our system. If you do not have a contractor, select one from our list.'
                                                arrow
                                                placement='right'
                                            >
                                                <IconButton
                                                    size='small'
                                                    sx={{ p: 0.5 }}
                                                >
                                                    <InfoOutlinedIcon
                                                        fontSize='inherit'
                                                        sx={{
                                                            fontSize: 16,
                                                            opacity: 0.6,
                                                        }}
                                                    />
                                                </IconButton>
                                            </CustomTooltip>
                                        </Stack>

                                        <FormAutocomplete
                                            name='contractorId'
                                            control={control}
                                            options={contractorOptions}
                                            renderOption={(
                                                props,
                                                option: ContractorOption,
                                            ) => (
                                                <li key={option.id} {...props}>
                                                    <Stack
                                                        direction='row'
                                                        spacing={1}
                                                        alignItems='center'
                                                        sx={{
                                                            width: '100%',
                                                            py: 0.5,
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{ flex: 1 }}
                                                        >
                                                            {option.label}
                                                        </Typography>
                                                        {option.type ? (
                                                            <CustomSmallListChip
                                                                color='green'
                                                                size='small'
                                                                label={
                                                                    option.type
                                                                }
                                                            />
                                                        ) : null}
                                                    </Stack>
                                                </li>
                                            )}
                                            noOptionsText={
                                                <Typography
                                                    sx={{ fontSize: '12px' }}
                                                >
                                                    No contractors available
                                                </Typography>
                                            }
                                        />
                                    </Box>

                                    <Divider>OR</Divider>
                                </>
                            )}
                            <Typography variant='body3' color='text.secondary'>
                                {hasExistingContractors
                                    ? 'Want to use your own contractor? If they’re not on 7DAY, invite them below and we’ll email them instructions to join your workspace.'
                                    : 'Prefer to use your own contractor? Invite them to join 7DAY and manage your jobs seamlessly.'}
                            </Typography>
                            <Box>
                                <InputLabel sx={labelSx} required>
                                    Company name
                                </InputLabel>
                                <FormTextField
                                    name='contractor.name'
                                    control={control}
                                />
                            </Box>

                            <Box>
                                <InputLabel sx={labelSx} required>
                                    Email
                                </InputLabel>
                                <FormTextField
                                    name='contractor.email'
                                    control={control}
                                />
                                <FormHelperText
                                    sx={{ fontSize: '10px', fontWeight: 500 }}
                                >
                                    This is where the invitation will be sent.
                                </FormHelperText>
                            </Box>

                            <Box>
                                <InputLabel sx={labelSx} required>
                                    Primary contact
                                </InputLabel>
                                <FormTextField
                                    name='contractor.primaryContact'
                                    control={control}
                                />
                            </Box>

                            <Box>
                                <InputLabel sx={labelSx}>
                                    Permissions
                                </InputLabel>
                                <Controller
                                    name='permissions'
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <FormControl
                                            error={Boolean(fieldState.error)}
                                            sx={{ mt: 1 }}
                                        >
                                            <RadioGroup
                                                value={field.value}
                                                onChange={(e) =>
                                                    field.onChange(
                                                        e.target.value,
                                                    )
                                                }
                                            >
                                                <FormControlLabel
                                                    value='readOnly'
                                                    control={<Radio />}
                                                    label={
                                                        <Box>
                                                            <Typography
                                                                sx={labelSx}
                                                            >
                                                                Read only
                                                            </Typography>
                                                            <Typography
                                                                variant='body3'
                                                                color='text.secondary'
                                                            >
                                                                Can view jobs
                                                                and
                                                                reservations.
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                                <FormControlLabel
                                                    value='readAndWrite'
                                                    control={<Radio />}
                                                    label={
                                                        <Box>
                                                            <Typography
                                                                sx={labelSx}
                                                            >
                                                                Read and write
                                                            </Typography>
                                                            <Typography
                                                                variant='body3'
                                                                color='text.secondary'
                                                            >
                                                                Can create and
                                                                edit jobs and
                                                                updates.
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                            </RadioGroup>
                                            {fieldState.error?.message ? (
                                                <FormHelperText>
                                                    {fieldState.error.message}
                                                </FormHelperText>
                                            ) : null}
                                        </FormControl>
                                    )}
                                />
                            </Box>
                        </Stack>
                    </DialogContent>

                    <DialogActions>
                        <Button
                            variant='outlined'
                            onClick={handleClose}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant='contained'
                            type='submit'
                            disabled={isSubmitting}
                        >
                            Send invite
                        </Button>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};

export default ContractorInviteForm;
